/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';

import { withNotification } from 'components/Notification';
import { NotificationTypes, apiErrorHandler } from 'utils';
import { useStore } from 'context/store-context';
import { GET_STORE_TRACKING_PAGE_SETTINGS } from 'gql/queries';
import { UPDATE_STORE_TRACKING_PAGE_SETTINGS } from 'gql/mutations';

import ColorPicker from '../../EmbedClaimPortal/ColorPicker';
import ImageUploader from '../../EmbedClaimPortal/ImageUploader';
import SectionPanel from '../../EmbedClaimPortal/Section';
import { Toggle } from 'components/Toggle';

import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';

import PreviewPane from './PreviewPane';
import { ShopifyProductPicker, ProductSnapshot } from './ShopifyProductPicker';
import {
  AD_PLACEMENT_OPTIONS,
  CORNER_OPTIONS,
  COVER_POSITION_OPTIONS,
  DEFAULT_TRACKING_PAGE_CONFIG,
  FONT_OPTIONS,
  TrackingPageAd,
  TrackingPageConfig,
  withDefaults,
} from './defaultConfig';

/**
 * Native editor for the shopper-facing tracking page, replacing the Unlayer
 * drag-and-drop canvas.
 *
 * Saving is explicit rather than on every keystroke: the config is validated and
 * persisted server-side by `saveStoresSettings`, and an autosave would fire a
 * mutation per character.
 */

/** Visibility switch rendered in a panel header, beside the collapse chevron. */
const VisibilityToggle = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <Toggle isEnabled={checked} onChange={onChange} ariaLabel={label} isSmall />
);

/** Mirrors the API's `normaliseAds`, which drops slots missing either value. */
const isAdComplete = (ad: TrackingPageAd) =>
  Boolean(ad.imageUrl?.trim()) && Boolean(ad.ctaUrl?.trim());

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="mb-4">
    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">
      {label}
    </span>
    {children}
  </div>
);

export const TrackingPageEditor = withNotification(
  ({ showNotification }: any) => {
    const { storeId } = useStore();

    const [saved, setSaved] = useState<TrackingPageConfig>(
      DEFAULT_TRACKING_PAGE_CONFIG,
    );
    const [config, setConfig] = useState<TrackingPageConfig>(
      DEFAULT_TRACKING_PAGE_CONFIG,
    );
    const [pickerFor, setPickerFor] = useState<number | null>(null);
    const [refreshingAd, setRefreshingAd] = useState<number | null>(null);

    const isDirty = useMemo(
      () => JSON.stringify(saved) !== JSON.stringify(config),
      [saved, config],
    );

    const [getSettings] = useLazyQuery(GET_STORE_TRACKING_PAGE_SETTINGS, {
      fetchPolicy: 'cache-and-network',
      onError(error) {
        showNotification(
          NotificationTypes.error,
          apiErrorHandler(error)?.message,
        );
      },
      onCompleted(data) {
        const stored = data?.storesSettings?.[0]?.trackingPageConfig;
        const next = withDefaults(stored);
        setSaved(next);
        setConfig(next);
      },
    });

    const [saveSettings, { loading: saving }] = useMutation(
      UPDATE_STORE_TRACKING_PAGE_SETTINGS,
      {
        onError(error) {
          showNotification(
            NotificationTypes.error,
            apiErrorHandler(error)?.message,
          );
        },
      },
    );

    useEffect(() => {
      if (storeId) getSettings({ variables: { storeId } });
    }, [storeId]);

    // Unsaved theme work is easy to lose by navigating away mid-edit.
    useEffect(() => {
      if (!isDirty) return undefined;

      const warn = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = '';
      };

      window.addEventListener('beforeunload', warn);
      return () => window.removeEventListener('beforeunload', warn);
    }, [isDirty]);

    const isThemeDefault =
      JSON.stringify(config.theme) ===
      JSON.stringify(DEFAULT_TRACKING_PAGE_CONFIG.theme);

    const patchTheme = (partial: Partial<TrackingPageConfig['theme']>) =>
      setConfig((current) => ({
        ...current,
        theme: { ...current.theme, ...partial },
      }));

    const patchBranding = (partial: Partial<TrackingPageConfig['branding']>) =>
      setConfig((current) => ({
        ...current,
        branding: { ...current.branding, ...partial },
      }));

    const patchCopy = (partial: Partial<TrackingPageConfig['copy']>) =>
      setConfig((current) => ({
        ...current,
        copy: { ...current.copy, ...partial },
      }));

    const toggleSection = (key: keyof TrackingPageConfig['sections']) =>
      setConfig((current) => ({
        ...current,
        sections: { ...current.sections, [key]: !current.sections[key] },
      }));

    const updateAd = (index: number, partial: Partial<TrackingPageAd>) =>
      setConfig((current) => ({
        ...current,
        ads: current.ads.map((ad, i) =>
          i === index ? { ...ad, ...partial } : ad,
        ),
      }));

    const addAd = () =>
      setConfig((current) => ({
        ...current,
        // Turning the section on here saves the merchant from filling in an ad
        // and still seeing nothing on the tracking page.
        sections: { ...current.sections, ads: true },
        ads: [
          ...current.ads,
          {
            id: `ad-${Date.now()}`,
            productId: null,
            imageUrl: '',
            headline: '',
            body: '',
            ctaLabel: 'Shop now',
            ctaUrl: '',
            placement: 'below_timeline',
          },
        ],
      }));

    const applySnapshot = (index: number, snapshot: ProductSnapshot) => {
      updateAd(index, {
        productId: snapshot.productId,
        imageUrl: snapshot.imageUrl,
        headline: snapshot.headline,
        body: snapshot.body ?? '',
        ctaUrl: snapshot.ctaUrl,
      });
      setPickerFor(null);
    };

    const removeAd = (index: number) =>
      setConfig((current) => ({
        ...current,
        ads: current.ads.filter((_, i) => i !== index),
      }));

    const handleDiscard = () => setConfig(saved);

    const handleSave = async () => {
      if (!storeId || !isDirty) return;

      try {
        await saveSettings({
          variables: { input: { storeId, trackingPageConfig: config } },
        });
        setSaved(config);
        showNotification(NotificationTypes.success, 'Tracking page updated.');
      } catch {
        // Surfaced by the mutation's onError handler.
      }
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6 items-start">
        <div className="min-w-0">
          {/* First panel because the cover is the first thing a shopper sees. */}
          <SectionPanel title="Cover image">
            <Field label="Image">
              <div className="[&>div>label]:sr-only">
                <ImageUploader
                  label="cover image"
                  image={config.branding.coverImageUrl || ''}
                  setImage={(url: string) =>
                    patchBranding({ coverImageUrl: url || null })
                  }
                />
              </div>
            </Field>
            <Field label="Position">
              <select
                aria-label="Cover image side"
                value={config.branding.coverImagePosition}
                onChange={(e) =>
                  patchBranding({ coverImagePosition: e.target.value as any })
                }
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
              >
                {COVER_POSITION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </SectionPanel>

          <SectionPanel title="Branding">
            <Field label="Logo">
              <div className="[&>div>label]:sr-only">
                <ImageUploader
                  label="logo"
                  image={config.branding.logoUrl || ''}
                  setImage={(url: string) =>
                    patchBranding({ logoUrl: url || null })
                  }
                />
              </div>
            </Field>
            <Field label="Logo height (px)">
              <NonNegativeNumberInput
                min={16}
                max={120}
                aria-label="Logo height"
                value={config.branding.logoHeight}
                onChange={(e) =>
                  patchBranding({ logoHeight: Number(e.target.value) })
                }
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-28"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={config.branding.showPoweredBy}
                onChange={(e) =>
                  patchBranding({ showPoweredBy: e.target.checked })
                }
              />
              Show &ldquo;Tracking powered by ShipAid&rdquo;
            </label>
          </SectionPanel>

          <SectionPanel
            title="Status header"
            headerAction={
              <VisibilityToggle
                label="Show status header"
                checked={config.sections.hero}
                onChange={() => toggleSection('hero')}
              />
            }
          >
            <p className="text-sm text-gray-500 mb-4">
              The current status, estimated delivery and tracking number.
            </p>
            <Field label="Headline (optional)">
              <input
                type="text"
                aria-label="Headline"
                value={config.copy.headline || ''}
                onChange={(e) =>
                  patchCopy({ headline: e.target.value || null })
                }
                placeholder="Thanks for your order!"
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
              />
            </Field>
          </SectionPanel>

          <SectionPanel title="Theme">
            <div className="flex justify-end mb-2">
              <button
                type="button"
                aria-label="Reset theme to default"
                onClick={() =>
                  patchTheme({ ...DEFAULT_TRACKING_PAGE_CONFIG.theme })
                }
                disabled={isThemeDefault}
                className="text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                Reset theme to default
              </button>
            </div>
            <ColorPicker
              label="Primary colour"
              color={config.theme.primaryColor}
              setColor={(value: string) => patchTheme({ primaryColor: value })}
            />
            <ColorPicker
              label="Background colour"
              color={config.theme.backgroundColor}
              setColor={(value: string) =>
                patchTheme({ backgroundColor: value })
              }
            />
            <ColorPicker
              label="Card colour"
              color={config.theme.surfaceColor}
              setColor={(value: string) => patchTheme({ surfaceColor: value })}
            />
            <ColorPicker
              label="Text colour"
              color={config.theme.textColor}
              setColor={(value: string) => patchTheme({ textColor: value })}
            />
            <Field label="Font">
              <select
                aria-label="Font"
                value={config.theme.fontFamily}
                onChange={(e) =>
                  patchTheme({ fontFamily: e.target.value as any })
                }
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
              >
                {FONT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Corners">
              <select
                aria-label="Corners"
                value={config.theme.cornerRadius}
                onChange={(e) =>
                  patchTheme({ cornerRadius: e.target.value as any })
                }
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
              >
                {CORNER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </SectionPanel>

          <SectionPanel title="Page sections">
            {(
              [
                [
                  'progress',
                  'Progress bar',
                  'The four-step rail from ordered through delivered',
                ],
                [
                  'timeline',
                  'Tracking history',
                  'Every carrier scan, grouped by day',
                ],
                ['items', 'Items', 'The products included in the shipment'],
              ] as const
            ).map(([key, label, hint]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <span className="block text-sm font-medium text-[#202223]">
                    {label}
                  </span>
                  <span className="block text-sm text-gray-500">{hint}</span>
                </div>
                <VisibilityToggle
                  label={`Show ${label.toLowerCase()}`}
                  checked={config.sections[key]}
                  onChange={() => toggleSection(key)}
                />
              </div>
            ))}
          </SectionPanel>

          <SectionPanel
            title="Support"
            headerAction={
              <VisibilityToggle
                label="Show support footer"
                checked={config.sections.support}
                onChange={() => toggleSection('support')}
              />
            }
          >
            <Field label="Support email">
              <input
                type="email"
                aria-label="Support email"
                value={config.copy.supportEmail || ''}
                onChange={(e) =>
                  patchCopy({ supportEmail: e.target.value || null })
                }
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
              />
            </Field>
            <Field label="Support page URL">
              <input
                type="url"
                aria-label="Support page URL"
                value={config.copy.supportUrl || ''}
                onChange={(e) =>
                  patchCopy({ supportUrl: e.target.value || null })
                }
                placeholder="https://"
                className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
              />
            </Field>
          </SectionPanel>

          <SectionPanel
            title="Promotions"
            headerAction={
              <VisibilityToggle
                label="Show promotions"
                checked={config.sections.ads}
                onChange={() => toggleSection('ads')}
              />
            }
          >
            <p className="text-sm text-gray-500 mb-3">
              Promote products or offers on the tracking page. Links must be
              http(s).
            </p>
            {config.ads.map((ad, index) => (
              <div
                key={ad.id}
                className="border border-gray-200 rounded-md p-3 mb-3"
              >
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() =>
                      setPickerFor(pickerFor === index ? null : index)
                    }
                    className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50"
                  >
                    Pick from Shopify
                  </button>
                  {ad.productId ? (
                    <button
                      type="button"
                      onClick={() => setRefreshingAd(index)}
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50"
                    >
                      Refresh from Shopify
                    </button>
                  ) : null}
                </div>

                <Field label="Image">
                  <div className="[&>div>label]:sr-only">
                    <ImageUploader
                      label={`promotion ${index + 1} image`}
                      image={ad.imageUrl}
                      setImage={(url: string) =>
                        updateAd(index, { imageUrl: url })
                      }
                    />
                  </div>
                </Field>
                <Field label="Headline">
                  <input
                    type="text"
                    aria-label={`Promotion ${index + 1} headline`}
                    value={ad.headline || ''}
                    onChange={(e) =>
                      updateAd(index, { headline: e.target.value })
                    }
                    className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
                  />
                </Field>
                <Field label="Body">
                  <input
                    type="text"
                    aria-label={`Promotion ${index + 1} body`}
                    value={ad.body || ''}
                    onChange={(e) => updateAd(index, { body: e.target.value })}
                    className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
                  />
                </Field>
                <Field label="Button label">
                  <input
                    type="text"
                    aria-label={`Promotion ${index + 1} button label`}
                    value={ad.ctaLabel || ''}
                    onChange={(e) =>
                      updateAd(index, { ctaLabel: e.target.value })
                    }
                    className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
                  />
                </Field>
                <Field label="Destination URL">
                  <input
                    type="url"
                    aria-label="Destination URL"
                    value={ad.ctaUrl}
                    onChange={(e) =>
                      updateAd(index, { ctaUrl: e.target.value })
                    }
                    placeholder="https://"
                    className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
                  />
                </Field>
                <Field label="Placement">
                  <select
                    aria-label={`Promotion ${index + 1} placement`}
                    value={ad.placement}
                    onChange={(e) =>
                      updateAd(index, { placement: e.target.value as any })
                    }
                    className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full"
                  >
                    {AD_PLACEMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {!isAdComplete(ad) ? (
                  <p className="text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2 mb-3">
                    This slot needs an image and a destination URL to appear in
                    the preview. Incomplete slots are not saved.
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => removeAd(index)}
                  className="text-sm text-red-600 hover:underline"
                >
                  Remove promotion
                </button>
              </div>
            ))}

            {/* Re-picking is how a snapshot is refreshed: the merchant sees the
                current Shopify data and confirms it. */}
            <ShopifyProductPicker
              show={pickerFor !== null || refreshingAd !== null}
              storeId={storeId as string}
              onSelect={(snapshot) => {
                const activeIndex = pickerFor ?? refreshingAd;
                if (activeIndex !== null) applySnapshot(activeIndex, snapshot);
                setRefreshingAd(null);
              }}
              onClose={() => {
                setPickerFor(null);
                setRefreshingAd(null);
              }}
            />

            {config.ads.length < 4 ? (
              <button
                type="button"
                onClick={addAd}
                className="px-3 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50"
              >
                Add promotion
              </button>
            ) : null}
          </SectionPanel>
        </div>

        <div className="min-w-0 lg:sticky lg:top-6 order-first lg:order-none">
          <PreviewPane config={config} />
        </div>

        {/* Easy to tweak a dozen settings and wander off without saving, and the
          preview updates live so nothing on screen signals the difference. */}
        {isDirty ? (
          <div
            role="status"
            aria-label="Unsaved changes"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 rounded-lg bg-gray-900 text-white shadow-lg px-5 py-3"
          >
            <span className="text-sm font-medium">
              You have unsaved changes
            </span>
            <button
              type="button"
              onClick={handleDiscard}
              disabled={saving}
              className="text-sm text-gray-300 hover:text-white disabled:opacity-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 rounded-md text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </div>
    );
  },
);

export default TrackingPageEditor;
